import { Button, type TabItem, TabPanel, TabView } from '@ics-portal/guardian';
import { clsx } from 'clsx';
import {
    type ForwardedRef,
    forwardRef,
    type ReactNode,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import s from './TabbedInfoCard.module.scss';

export interface TabbedInfoCardApi {
    setActiveTab: (tabName: string) => void;
}

interface TabbedInfoCardProps {
    selectedKey?: string;
    onSelectionChange?: (selectedKey: string) => void;
    tabItems: TabItem[];
}

function findTabIndex(tabItems: TabItem[], tabName?: string): number {
    if (!tabName) {
        return 0;
    }
    const index = tabItems.findIndex((tab) => tab.name === tabName);
    return index !== -1 ? index : 0;
}

function TabbedInfoCardComponent(
    { selectedKey, onSelectionChange, tabItems }: TabbedInfoCardProps,
    forwardedRef: ForwardedRef<TabbedInfoCardApi>,
): ReactNode {
    const initialSelectedKeyApplied = useRef(false);
    const prevSelectedKey = useRef(selectedKey);
    const prevTabCount = useRef(tabItems.length);

    const [activeTabName, setActiveTabName] = useState<string | undefined>(
        () =>
            tabItems[findTabIndex(tabItems, selectedKey)]?.name ?? selectedKey,
    );

    const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => {
        const initialName =
            tabItems[findTabIndex(tabItems, selectedKey)]?.name ?? selectedKey;
        return new Set(initialName ? [initialName] : []);
    });

    const markVisited = useCallback((tabName: string): void => {
        setVisitedTabs((prev) => {
            if (prev.has(tabName)) {
                return prev;
            }
            return new Set(prev).add(tabName);
        });
    }, []);

    const activeIndex = findTabIndex(tabItems, activeTabName);

    useImperativeHandle(forwardedRef, () => ({
        setActiveTab: (tabName: string) => {
            const index = tabItems.findIndex((tab) => tab.name === tabName);
            if (index !== -1) {
                setActiveTabName(tabName);
                markVisited(tabName);
            }
        },
    }));

    // A single-tab list (e.g. guidance-only placeholder) has no user-chosen
    // active tab; when it grows, show the new first tab like an index-based
    // active tab would.
    useEffect(() => {
        const previousCount = prevTabCount.current;
        prevTabCount.current = tabItems.length;
        if (previousCount !== 1 || tabItems.length < 2) {
            return;
        }
        const [firstTab] = tabItems;
        setActiveTabName(firstTab.name);
        markVisited(firstTab.name);
    }, [markVisited, tabItems]);

    useEffect(() => {
        if (!selectedKey) {
            return;
        }

        if (
            !initialSelectedKeyApplied.current ||
            selectedKey !== prevSelectedKey.current
        ) {
            const index = tabItems.findIndex((tab) => tab.name === selectedKey);
            if (index !== -1) {
                setActiveTabName(selectedKey);
                markVisited(selectedKey);
                initialSelectedKeyApplied.current = true;
                prevSelectedKey.current = selectedKey;
            }
        }
    }, [markVisited, selectedKey, tabItems]);

    useEffect(() => {
        const currentActiveTab = tabItems[activeIndex];
        if (!currentActiveTab) {
            return;
        }
        if (activeTabName !== currentActiveTab.name) {
            setActiveTabName(currentActiveTab.name);
        }
        markVisited(currentActiveTab.name);
    }, [activeIndex, activeTabName, markVisited, tabItems]);

    function handleTabChange(newIndex: number): void {
        const newTab = tabItems[newIndex];
        setActiveTabName(newTab.name);
        markVisited(newTab.name);
        if (onSelectionChange) {
            onSelectionChange(newTab.name);
        }
    }

    return (
        <TabView
            className={clsx(s.tabView, 'h-full bg-n20 flex flex-column')}
            activeIndex={activeIndex}
            renderActiveOnly={false}
            onTabChange={(e) => {
                handleTabChange(e.index);
            }}
        >
            {tabItems.map((tabItem, index) => (
                <TabPanel
                    className={s.tabPanel}
                    key={tabItem.name}
                    headerTemplate={
                        <Button
                            className={clsx(s.tabButton, {
                                [s.active]: index === activeIndex,
                            })}
                            aria-label={tabItem.name}
                            icon={tabItem.icon}
                            tooltip={tabItem.tooltip}
                            data-pr-position="bottom"
                            onClick={() => {
                                handleTabChange(index);
                            }}
                        />
                    }
                >
                    {visitedTabs.has(tabItem.name) ? tabItem.content : null}
                </TabPanel>
            ))}
        </TabView>
    );
}

export const TabbedInfoCard = forwardRef(TabbedInfoCardComponent);
export default TabbedInfoCard;

export { default as InfoCardWrapper } from './Layout/InfoCardWrapper';
